import { mapGetters } from 'vuex';
import { calculateTotalDistribution } from '@/utilities';
import {
  assets,
  liabilities,
  beneficiaries,
  charities,
  executors,
  gifts,
  guardians,
  people,
  pets,
  subscription,
  user,
  will,
} from '@/mixins/apollo';

export default {
  name: 'MixinsWillProgress',
  mixins: [
    assets,
    liabilities,
    beneficiaries,
    charities,
    executors,
    gifts,
    guardians,
    people,
    pets,
    subscription,
    user,
    will,
  ],
  computed: {
    ...mapGetters('modules', ['modules']),
    totalSteps() {
      let total = 0;
      this.modules.forEach((module) => {
        total += module.totalSteps;
      });
      return total;
    },
    completedSteps() {
      return {
        'About Yourself': (() => {
          let steps = 0;
          if (this.willMeta) {
            if (
              this.willMeta.has_alt_name === false ||
              (this.willMeta.alt_name_first && this.willMeta.alt_name_last)
            ) {
              steps++;
            }
            if (this.willMeta.date_of_birth) {
              steps++;
            }
            if (
              this.willMeta.address_street &&
              this.willMeta.address_suburb &&
              this.willMeta.address_state &&
              this.willMeta.address_postcode
            ) {
              steps++;
            }
            if (this.willMeta.has_partner === false || this.partners.length) {
              steps++;
              // Phone
              steps++;
            }
          }
          return steps;
        })(),
        Guardians: (() => {
          let steps = 0;
          if (this.willMeta) {
            if (this.willMeta.has_children === false || this.children.length) {
              steps++;
            }
            if (this.willMeta.has_children && !this.minors.length) {
              steps += 2;
            }
            if (
              this.willMeta.has_children === false ||
              this.willMeta.has_primary_guardian === false ||
              this.primaryGuardians.length
            ) {
              steps++;
            }
            if (
              this.willMeta.has_children === false ||
              this.willMeta.has_primary_guardian === false ||
              this.willMeta.has_backup_guardian === false ||
              this.backupGuardians.length
            ) {
              steps++;
            }
            if (this.willMeta.has_pets === false || this.pets.length) {
              steps++;
            }
            if (
              this.willMeta.has_pets === false ||
              this.willMeta.has_pet_guardians === false ||
              (this.pets.length &&
                this.willMeta.has_pet_guardians &&
                this.pets.every((pet) => pet.person && pet.person.id))
            ) {
              steps++;
              // Pet Care Fund
              steps++;
            }
          }
          return steps;
        })(),
        Executors: (() => {
          let steps = 0;
          if (this.willMeta) {
            if (this.willMeta.executors_option) {
              steps++;
              steps++;

              switch (this.willMeta.executors_option) {
                case 'friendsFamily':
                  if (this.primaryExecutors.length) {
                    steps++;
                  }

                  if (
                    this.willMeta.has_backup_executor === false ||
                    this.backupExecutors.length
                  ) {
                    steps++;
                  }
                  break;
                case 'professional':
                  if (
                    this.willMeta.professional_executor_terms_check &&
                    this.willMeta.professional_executor_payment_check
                  ) {
                    steps++;
                    if (
                      this.willMeta.has_backup_executor === true &&
                      this.backupExecutors.length
                    ) {
                      steps++;
                    }
                  }
                  break;
                case 'friendsFamilyAndProfessional':
                  if (this.primaryExecutors.length) {
                    steps++;
                  }

                  if (
                    this.willMeta.professional_executor_terms_check &&
                    this.willMeta.professional_executor_payment_check
                  ) {
                    steps++;
                  }
                  break;
                default:
                  break;
              }
            }
          }
          return steps;
        })(),
        Estate: (() => {
          let steps = 0;
          if (this.willMeta) {
            if (this.willMeta.estate_split) {
              steps++;
              steps++;
              if (this.validEstateBackup) {
                steps++;
                steps++;
              }

              if (
                (this.willMeta.estate_split === 'custom' &&
                  this.validEstateDistribution) ||
                this.willMeta.estate_split === 'partner' ||
                this.willMeta.estate_split === 'children'
              ) {
                steps++;
              }

              if (
                (this.willMeta.referral_charity &&
                  this.willMeta.charity_in_estate !== null) ||
                this.willMeta.estate_split
              ) {
                steps++;
              }
              if (this.beneficiaries && this.beneficiaries.length) {
                const totalDistributions = calculateTotalDistribution(
                  this.beneficiaries.map(
                    (beneficiary) => beneficiary.distribution
                  )
                );

                if (
                  (this.will && this.will.isPrimaryEstateSplitEvenly) ||
                  totalDistributions === 100
                ) {
                  steps++;
                }
              }
              let isBackupBeneficiariesValid = true;
              for (const beneficiary of this.beneficiaries) {
                if (beneficiary.backup.length) {
                  const totalBackupBeneficiary = calculateTotalDistribution(
                    beneficiary.backup.map(
                      (backupBeneficiary) => backupBeneficiary.distribution
                    )
                  );

                  if (
                    !beneficiary.isBackupEstateSplitEvenly &&
                    totalBackupBeneficiary !== 100
                  ) {
                    isBackupBeneficiariesValid = false;
                    break;
                  }
                }
              }
              if (isBackupBeneficiariesValid) {
                steps++;
              }

              if (steps > 5) {
                steps++;
                steps++;
              }
            }
          }

          if (steps > 5) {
            steps += 2;
          }
          return steps;
        })(),
        Gifts: (() => {
          let steps = 0;
          if (this.willMeta) {
            if (this.willMeta.has_gifts === false || this.gifts.length) {
              steps++;

              if (this.willMeta.referral_charity) {
                if (
                  this.willMeta.charity_in_estate !== null ||
                  this.willMeta.charity_in_gifts !== null
                ) {
                  steps++;
                }
              } else {
                steps++;
              }
            }
          }

          return steps;
        })(),
        Assets: (() => {
          let steps = 0;
          if (this.willMeta) {
            if (this.willMeta.has_assets === false || this.assets.length) {
              steps++;
              steps++; // Step again for optional notes step
            }
            if (
              this.willMeta.has_liabilities === false ||
              this.liabilities.length
            ) {
              steps++;
              steps++; // Step again for optional notes step
            }
          }
          return steps;
        })(),
        Funeral: (() => {
          let steps = 0;
          if (this.willMeta) {
            if (
              this.willMeta.funeral_skip === true ||
              this.willMeta.funeral_type !== null
            ) {
              steps++;
              steps++;
              steps++;
              steps++;
            }
          }
          return steps;
        })(),
      };
    },
    completedWillStepsAsPercentage() {
      const stepNames = Object.keys(this.completedSteps);
      const completedSteps = stepNames.reduce(
        (total, stepName) => total + this.completedSteps[stepName],
        0
      );
      return (completedSteps * 100) / this.totalSteps;
    },
    validEstateDistribution() {
      if (this.will && this.will.isPrimaryEstateSplitEvenly) {
        return true;
      }

      return this.beneficiaries.every(
        (beneficiary) =>
          beneficiary.distribution && beneficiary.distribution !== '0'
      );
    },
    validEstateBackup() {
      return this.beneficiaries.every((beneficiary) => {
        if (beneficiary.charity) return true;
        if (beneficiary.meta.estate_backup_split === 'custom') {
          return beneficiary.backup.every((b) => {
            return (
              beneficiary.isBackupEstateSplitEvenly ||
              (b.distribution && b.distribution !== '0')
            );
          });
        } else {
          return !!beneficiary.meta.estate_backup_split;
        }
      });
    },
  },
};
